import React, { type SyntheticEvent } from 'react';
import noop from 'lodash/noop';
import { useAnalyticsEvents, type UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Button, { LoadingButton } from '@atlaskit/button';
import { ModalBody, ModalFooter, ModalHeader, ModalTitle } from '@atlaskit/modal-dialog';
import { expVal } from '@atlassian/jira-feature-experiments';
import ShortcutScope from '@atlassian/jira-common-components-keyboard-shortcuts/src/shortcut-scope.tsx';
import { SERVICE_DESK_PROJECT } from '@atlassian/jira-common-constants/src/project-types.tsx';
import { withFlagService } from '@atlassian/jira-flags';
import { injectIntlV2 as injectIntl } from '@atlassian/jira-intl/src/v2/inject.tsx';
import {
	ContextualAnalyticsData,
	FireScreenAnalytics,
	fireUIAnalytics,
	fireTrackAnalytics,
	MODAL,
} from '@atlassian/jira-product-analytics-bridge';
import { ModalDialog } from '@atlassian/jira-software-modal-dialog/src/ui/modal-dialog/index.tsx';
import type FetchError from '@atlassian/jira-fetch/src/utils/errors.tsx';
import { useProjectRestoreService } from '../services/index.tsx';
import messages from './messages.tsx';
import { ModalMessageContent } from './modal-message/index.tsx';
import type { PropsWithIntlAndFlagService } from './types.tsx';
import { getDurationInTrash } from './utils.tsx';

export const RESTORE_FROM_TRASH = 'restore-from-trash' as const;
export const RESTORE_FROM_ARCHIVE = 'restore-from-archive' as const;
export type ProjectRestoreType = typeof RESTORE_FROM_TRASH | typeof RESTORE_FROM_ARCHIVE;

export const ProjectRestoreModal = (props: PropsWithIntlAndFlagService) => {
	const jsmModalHeaderGalaxia = expVal('project-terminology-refresh', 'isEnabled', false)
		? messages.jsmModalHeaderGalaxia
		: messages.jsmModalHeader;
	const modalHeaderGalaxia = expVal('project-terminology-refresh', 'isEnabled', false)
		? messages.modalHeaderGalaxia
		: messages.modalHeader;
	const {
		isProjectSimplified,
		projectType,
		projectName,
		projectId,
		projectKey,
		projectInactiveDate,
		intl: { formatMessage },
		onClose,
		onProjectRestoreSuccess,
		onProjectRestoreFailure,
		flagService: { showFlag },
	} = props;

	const [isRestoring, restoreProject] = useProjectRestoreService({
		projectId,
	});

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const attributes = {
		projectType,
		isProjectSimplified,
	};

	const onSuccess = (): void => {
		const analyticsEvent: UIAnalyticsEvent = createAnalyticsEvent({
			action: 'restored',
		});
		fireTrackAnalytics(analyticsEvent, 'project restored', projectId.toString(), {
			...attributes,
			durationInTrash: projectInactiveDate ? getDurationInTrash(projectInactiveDate) : undefined,
		});
		onProjectRestoreSuccess();
		if (projectType === SERVICE_DESK_PROJECT) {
			showFlag({
				messageId: 'project-restore-modal.ui.show-flag.success.restore-service-desk-success',
				messageType: 'transactional',
				type: 'success',
				title: expVal('project-terminology-refresh', 'isEnabled', false)
					? messages.restoreServiceDeskSuccessMessageTitleGalaxia
					: messages.restoreServiceDeskSuccessMessageTitle,
				description: expVal('project-terminology-refresh', 'isEnabled', false)
					? messages.restoreServiceDeskSuccessMessageContentGalaxia
					: messages.restoreServiceDeskSuccessMessageContent,
				testId: 'project-restore-modal.ui.flags.success-flag',
				actions: [
					{
						content: messages.restoreServiceDeskSuccessMessageActionEmailSettings,
						href: `/jira/servicedesk/projects/${projectKey}/settings/email`,
					},
				],
			});
		} else {
			showFlag({
				messageId: 'project-restore-modal.ui.show-flag.success.restore-success',
				messageType: 'transactional',
				type: 'success',
				title: expVal('project-terminology-refresh', 'isEnabled', false)
					? messages.restoreSuccessMessageTitleGalaxia
					: messages.restoreSuccessMessageTitle,
				testId: 'project-restore-modal.ui.flags.success-flag',
			});
		}
	};

	const onFailure = (error: Error | FetchError): void => {
		onProjectRestoreFailure(error);

		if (
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			(error as FetchError).statusCode?.toString().startsWith('4') &&
			error.message
		) {
			showFlag({
				messageId: 'project-restore-modal.ui.show-flag.error.restore-error',
				messageType: 'transactional',
				type: 'error',
				title: [messages.restoreErrorMessageTitle, { projectName }],
				description: [
					expVal('project-terminology-refresh', 'isEnabled', false)
						? messages.restoreErrorMessageDescriptionRenderErrorNonFinalGalaxia
						: messages.restoreErrorMessageDescriptionRenderErrorNonFinal,
					{
						projectName,
						error: error.message,
						br: () => <br />,
					},
				],
				testId: 'project-restore-modal.ui.flags.failure-flag',
			});
		} else {
			showFlag({
				messageId: 'project-restore-modal.ui.show-flag.error.restore-error.1',
				messageType: 'transactional',
				type: 'error',
				title: [messages.restoreErrorMessageTitle, { projectName }],
				description: [
					expVal('project-terminology-refresh', 'isEnabled', false)
						? messages.restoreErrorMessageDescriptionGalaxia
						: messages.restoreErrorMessageDescription,
					{ projectName },
				],
				testId: 'project-restore-modal.ui.flags.failure-flag',
			});
		}
	};

	const onRestore = (e: SyntheticEvent, analyticsEvent: UIAnalyticsEvent): void => {
		fireUIAnalytics(analyticsEvent, 'restoreProjectButton');
		restoreProject({ onSuccess, onFailure });
	};

	// Unstable components cause bugs and performance problems - please fix this! go/unstable-components
	// eslint-disable-next-line react/no-unstable-nested-components
	const ModalDialogDescription = () => (
		// eslint-disable-next-line @atlaskit/design-system/use-primitives-text
		<p>
			<ModalMessageContent projectType={projectType} />
		</p>
	);

	const onCancelOrNoop = !isRestoring ? onClose : noop;

	return (
		<ContextualAnalyticsData
			sourceName="restoreProject"
			sourceType={MODAL}
			attributes={attributes}
			containerId={`${projectId}`}
			containerType="project"
		>
			<ShortcutScope>
				<ModalDialog
					messageId="project-restore-modal.ui.modal-dialog"
					messageType="transactional"
					onClose={onCancelOrNoop}
					autoFocus={false}
					width="small"
				>
					<ModalHeader hasCloseButton>
						<ModalTitle>
							{formatMessage(
								projectType === SERVICE_DESK_PROJECT ? jsmModalHeaderGalaxia : modalHeaderGalaxia,
								{ projectName },
							)}
						</ModalTitle>
					</ModalHeader>
					<ModalBody>
						<FireScreenAnalytics />
						<ModalDialogDescription />
					</ModalBody>
					<ModalFooter>
						<Button
							appearance="subtle-link"
							onClick={onCancelOrNoop}
							isDisabled={isRestoring}
							testId="project-restore-modal.ui.close-button-wrapper"
						>
							{formatMessage(messages.cancelRestoreButton)}
						</Button>
						<LoadingButton
							appearance="primary"
							onClick={onRestore}
							isDisabled={isRestoring}
							isLoading={isRestoring}
							testId="project-restore-modal.ui.restore-button-wrapper"
						>
							{formatMessage(messages.confirmRestoreButton)}
						</LoadingButton>
					</ModalFooter>
				</ModalDialog>
			</ShortcutScope>
		</ContextualAnalyticsData>
	);
};

export const defaultProps = {
	isRestoring: false,
	onClose: noop,
} as const;

export default injectIntl(withFlagService(ProjectRestoreModal));
